import React, { useState, useRef, useCallback } from 'react';
import { MdMic, MdMicOff } from "react-icons/md";
import { primaryColor, VoiceButtonContainer } from '../mainPage/FAQSearch.style';

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

const VoiceButton = ({ onTranscript }) => {
  const [listening, setListening] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const recognitionRef = useRef(null);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const handleListen = useCallback(async () => {
    setErrorMsg('');

    if (listening) {
      stopListening();
      return;
    }

    if (!SpeechRecognitionAPI) {
      setErrorMsg('Speech recognition not supported in this browser.');
      return;
    }

    // Explicitly request mic permission first — this is what triggers the
    // browser permission dialog on TV/kiosk displays where SpeechRecognition
    // alone doesn't surface it.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release the stream immediately — we only needed it to trigger the prompt.
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMsg('Microphone access denied. Please allow mic access in browser settings.');
      } else if (err.name === 'NotFoundError') {
        setErrorMsg('No microphone found on this device.');
      } else {
        setErrorMsg('Microphone unavailable.');
      }
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognitionRef.current = recognition;

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setListening(true);

    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    recognition.onerror = (event) => {
      recognitionRef.current = null;
      setListening(false);
      if (event.error === 'not-allowed') {
        setErrorMsg('Microphone access denied.');
      } else if (event.error === 'no-speech') {
        setErrorMsg('No speech detected. Please try again.');
      } else if (event.error !== 'aborted') {
        setErrorMsg(`Voice error: ${event.error}`);
      }
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onTranscript(transcript);
    };

    recognition.start();
  }, [listening, stopListening, onTranscript]);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <VoiceButtonContainer onClick={handleListen}>
        {listening
          ? <MdMic size={20} color={primaryColor} />
          : <MdMicOff size={20} color={primaryColor} />}
      </VoiceButtonContainer>
      {errorMsg && (
        <div style={{
          fontSize: 11, color: '#D85A30', marginTop: 4,
          maxWidth: 180, textAlign: 'center', lineHeight: 1.3,
        }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
};

export default VoiceButton;
